import { useEffect, useState } from "react";

/**
 * Horário de atendimento, pedido de retorno e caixa postal do número (Calling API).
 * Consome os mesmos endpoints públicos do CRM (instance-auth via `api` do CallConsole):
 * GET /calls/settings, POST /calls/hours, POST /calls/voicemail(/announcement), POST /calls/settings.
 */

type Api = <T>(path: string, init?: RequestInit) => Promise<T>;
type Range = { open: string; close: string };
type Holiday = { date: string; start: string; end: string };

const DAYS: [string, string][] = [
  ["MONDAY", "Seg"], ["TUESDAY", "Ter"], ["WEDNESDAY", "Qua"], ["THURSDAY", "Qui"],
  ["FRIDAY", "Sex"], ["SATURDAY", "Sáb"], ["SUNDAY", "Dom"],
];
const TIMEZONES = [
  "America/Sao_Paulo", "America/Manaus", "America/Cuiaba", "America/Campo_Grande", "America/Porto_Velho",
  "America/Boa_Vista", "America/Rio_Branco", "America/Belem", "America/Fortaleza", "America/Recife",
  "America/Bahia", "America/Maceio", "America/Araguaina", "America/Noronha",
];
const DEFAULT_WEEK: Record<string, Range[]> = {
  MONDAY: [{ open: "08:00", close: "18:00" }], TUESDAY: [{ open: "08:00", close: "18:00" }],
  WEDNESDAY: [{ open: "08:00", close: "18:00" }], THURSDAY: [{ open: "08:00", close: "18:00" }],
  FRIDAY: [{ open: "08:00", close: "18:00" }], SATURDAY: [{ open: "08:00", close: "12:00" }], SUNDAY: [],
};

const hm = (t: string) => (t.includes(":") ? t : `${t.slice(0, 2)}:${t.slice(2)}`); // "0800" → "08:00"

function fileB64(f: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] ?? "");
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });
}

export function CallSettingsPanel({ api }: { api: Api }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // horário
  const [hoursOn, setHoursOn] = useState(false);
  const [tz, setTz] = useState("America/Sao_Paulo");
  const [week, setWeek] = useState<Record<string, Range[]>>(DEFAULT_WEEK);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [callback, setCallback] = useState(false);
  // caixa postal
  const [vmOn, setVmOn] = useState(false);
  const [vmTimeout, setVmTimeout] = useState(true);
  const [vmReject, setVmReject] = useState(false);
  const [vmSeconds, setVmSeconds] = useState(20);
  const [vmMedia, setVmMedia] = useState("");
  const [greetingUrl, setGreetingUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    api<any>("/calls/settings").then(s => {
      const c = s?.calling ?? {};
      setCallback(c.callback_permission_status === "ENABLED");
      const h = c.call_hours;
      if (h) {
        setHoursOn(h.status === "ENABLED");
        if (h.timezone_id) setTz(h.timezone_id);
        const w: Record<string, Range[]> = Object.fromEntries(DAYS.map(([d]) => [d, []]));
        for (const r of h.weekly_operating_hours ?? []) w[r.day_of_week]?.push({ open: hm(r.open_time), close: hm(r.close_time) });
        if ((h.weekly_operating_hours ?? []).length) setWeek(w);
        setHolidays((h.holiday_schedule ?? []).map((x: any) => ({ date: x.date, start: hm(x.start_time), end: hm(x.end_time) })));
      }
      const v = c.voicemail;
      if (v) {
        setVmOn(v.status === "ENABLED");
        setVmTimeout((v.triggers ?? []).includes("TIMEOUT"));
        setVmReject((v.triggers ?? []).includes("REJECT"));
        const d = v.audio?.default ?? {};
        if (d.timeout_seconds != null) setVmSeconds(d.timeout_seconds);
        if (d.announcement_media_id) setVmMedia(String(d.announcement_media_id));
      }
    }).catch(e => setMsg({ ok: false, text: e.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: ok }); }
    catch (e: any) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  }

  const post = (path: string, body: unknown) => api(path, { method: "POST", body: JSON.stringify(body) });

  const saveHours = () => run(() => post("/calls/hours", {
    status: hoursOn ? "ENABLED" : "DISABLED", timezone_id: tz,
    weekly_operating_hours: DAYS.flatMap(([d]) => week[d].map(r => ({ day_of_week: d, open_time: r.open, close_time: r.close }))),
    holiday_schedule: holidays.map(h => ({ date: h.date, start_time: h.start, end_time: h.end })),
  }), "Horário salvo na Meta (o app do cliente pode levar até 7 dias para mostrar).");

  const saveCallback = (on: boolean) => run(async () => {
    await post("/calls/settings", { calling: { callback_permission_status: on ? "ENABLED" : "DISABLED" } });
    setCallback(on);
  }, on ? "Pedido de retorno ligado." : "Pedido de retorno desligado.");

  const saveVoicemail = () => run(() => post("/calls/voicemail", {
    status: vmOn ? "ENABLED" : "DISABLED",
    triggers: [...(vmTimeout ? ["TIMEOUT"] : []), ...(vmReject ? ["REJECT"] : [])],
    timeout_seconds: vmSeconds, announcement_media_id: vmMedia || null,
  }), vmOn ? "Caixa postal ligada." : "Caixa postal desligada.");

  async function uploadGreeting(f: File | undefined) {
    if (!f) return;
    setGreetingUrl(URL.createObjectURL(f));
    await run(async () => {
      const r = await post("/calls/voicemail/announcement", { audio_b64: await fileB64(f) }) as { media_id: string };
      setVmMedia(r.media_id);
    }, "Saudação enviada. Clique em Salvar caixa postal para usar.");
  }

  const setRange = (d: string, i: number, k: keyof Range, v: string) =>
    setWeek(w => ({ ...w, [d]: w[d].map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));

  const input: React.CSSProperties = { padding: "4px 6px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink)", fontSize: 12, outline: "none" };
  const btn: React.CSSProperties = { background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink-secondary)", fontSize: 12, padding: "6px 12px", cursor: busy ? "wait" : "pointer", fontWeight: 600 };
  const link: React.CSSProperties = { background: "none", border: "none", color: "var(--info)", cursor: "pointer", fontSize: 12, padding: 0 };
  const h4: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" };
  const muted: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)" };

  return (
    <div style={{ borderTop: "1px solid var(--line)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      <button style={{ ...link, alignSelf: "flex-start", fontWeight: 600 }} onClick={() => setOpen(o => !o)}>
        {open ? "▾" : "▸"} Horário de atendimento e caixa postal
      </button>
      {open && <>
        {msg && <div style={{ fontSize: 12, color: msg.ok ? "var(--ok)" : "var(--danger)" }}>{msg.text}</div>}

        {/* Horário */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ ...h4, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={hoursOn} onChange={e => setHoursOn(e.target.checked)} /> Limitar ligações ao horário de atendimento
          </label>
          <span style={muted}>Fora do horário o cliente não consegue ligar: o WhatsApp mostra o próximo horário. Desligado = atende 24h.</span>
          {hoursOn && <>
            <select style={{ ...input, alignSelf: "flex-start" }} value={tz} onChange={e => setTz(e.target.value)}>
              {TIMEZONES.map(z => <option key={z} value={z}>{z.replace("America/", "").replace("_", " ")}</option>)}
            </select>
            {DAYS.map(([d, label]) => (
              <div key={d} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
                <span style={{ width: 30, color: "var(--ink)" }}>{label}</span>
                {week[d].length === 0 && <span style={muted}>fechado</span>}
                {week[d].map((r, i) => (
                  <span key={i} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <input type="time" style={input} value={r.open} onChange={e => setRange(d, i, "open", e.target.value)} />
                    –
                    <input type="time" style={input} value={r.close} onChange={e => setRange(d, i, "close", e.target.value)} />
                    <button style={link} title="Remover faixa" onClick={() => setWeek(w => ({ ...w, [d]: w[d].filter((_, j) => j !== i) }))}>×</button>
                  </span>
                ))}
                {week[d].length < 2 && (
                  <button style={link} onClick={() => setWeek(w => ({ ...w, [d]: [...w[d], w[d].length ? { open: "13:30", close: "18:00" } : { open: "08:00", close: "18:00" }] }))}>
                    + {week[d].length ? "faixa" : "abrir"}
                  </button>
                )}
              </div>
            ))}
            <span style={h4}>Feriados e exceções ({holidays.length}/20)</span>
            {holidays.map((h, i) => (
              <div key={i} style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12 }}>
                <input type="date" style={input} value={h.date} onChange={e => setHolidays(a => a.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
                <input type="time" style={input} value={h.start} onChange={e => setHolidays(a => a.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                –
                <input type="time" style={input} value={h.end} onChange={e => setHolidays(a => a.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                <button style={link} onClick={() => setHolidays(a => a.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            {holidays.length < 20 && (
              <button style={{ ...link, alignSelf: "flex-start" }} onClick={() => setHolidays(a => [...a, { date: "", start: "00:00", end: "23:59" }])}>+ feriado</button>
            )}
          </>}
          <button style={{ ...btn, alignSelf: "flex-start" }} disabled={busy} onClick={saveHours}>Salvar horário</button>
          <label style={{ ...muted, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={callback} disabled={busy} onChange={e => saveCallback(e.target.checked)} />
            Oferecer "pedir retorno" e pedir permissão de ligação a quem ligou (vale 7 dias)
          </label>
        </div>

        {/* Caixa postal */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, borderTop: "1px solid var(--line)", paddingTop: 10 }}>
          <label style={{ ...h4, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={vmOn} onChange={e => setVmOn(e.target.checked)} /> Caixa postal
          </label>
          <span style={muted}>A Meta atende sozinha, toca a saudação e grava o recado, que chega como áudio do cliente (vai pro agente e pros webhooks como qualquer áudio). Ligação feita pelo cliente é gratuita na Meta.</span>
          {vmOn && hoursOn && <span style={{ fontSize: 11, color: "var(--warn)" }}>Com horário ligado, a caixa postal só funciona dentro do horário (fora dele o cliente nem consegue ligar).</span>}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", fontSize: 12, color: "var(--ink)" }}>
            <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <input type="checkbox" checked={vmTimeout} onChange={e => setVmTimeout(e.target.checked)} /> Ninguém atendeu em
              <input type="number" min={0} max={30} style={{ ...input, width: 50 }} value={vmSeconds} onChange={e => setVmSeconds(Math.max(0, Math.min(30, Number(e.target.value))))} /> s
            </label>
            <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <input type="checkbox" checked={vmReject} onChange={e => setVmReject(e.target.checked)} /> Ligação recusada
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
            <label style={{ ...btn, display: "inline-block" }}>
              Enviar saudação (menos de 60 s)
              <input type="file" accept="audio/*" style={{ display: "none" }} disabled={busy} onChange={e => uploadGreeting(e.target.files?.[0])} />
            </label>
            <span style={muted}>{vmMedia ? `Saudação atual: ${vmMedia}` : "Nenhuma saudação enviada"}</span>
          </div>
          {greetingUrl && <audio controls src={greetingUrl} style={{ height: 32 }} />}
          <button style={{ ...btn, alignSelf: "flex-start" }} disabled={busy || (vmOn && (!vmMedia || (!vmTimeout && !vmReject)))} onClick={saveVoicemail}>
            Salvar caixa postal
          </button>
        </div>
      </>}
    </div>
  );
}
